import { interval, Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { MainScreen } from '../screens/MainScreen';
import { imageSize, zoomConfig } from '../../../common/data/config';
import { Zoom } from '../../../common/enums/zoom';
import { ZoomConfig } from '../../../common/enums/zoom-config';

export class MainScene extends Phaser.Scene {
  public zoom = 0.5;
  private maximalZoom = 3.5;
  private screen!: MainScreen;
  private minimalZoom!: number;
  private isZooming: boolean = false;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private zoomConfig: { [key: string]: ZoomConfig } = zoomConfig;

  constructor() {
    super({ key: 'main' });
  }

  create() {
    this.initialize();
  }

  public preload() {
    this.load.image('floor', 'assets/images/wall/floor.jpg');
    this.load.image('ground', 'assets/images/wall/ground.jpg');
    this.load.image('tile', 'assets/images/wall/tile.png');
    this.load.image('elevator', 'assets/images/wall/elevator.png');
    this.load.image(
      'toilet-icecream',
      'assets/images/wall/toilet-icecream.png'
    );
    this.load.image('elevator-frame', 'assets/images/wall/elevator-frame.png');
    this.load.image(
      'elevator-door-left',
      'assets/images/wall/elevator-door-left.png'
    );
    this.load.image(
      'elevator-door-right',
      'assets/images/wall/elevator-door-right.png'
    );
    this.load.animation('ratsData', 'assets/data/wall/rats.json');
    this.load.atlas(
      'rats',
      'assets/images/wall/rats.png',
      'assets/data/wall/rats.json'
    );
    this.load.json('staticTiles', 'assets/data/wall/static_tiles.json');
  }

  public zoomInIncessantly(): void {
    interval(16)
      .pipe(delay(200), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.isZooming = true;
        this.calculateAndSetZoom(this.zoomConfig.zoomInIncessantly);
      });
  }

  public zoomOutIncessantly(): void {
    interval(16)
      .pipe(delay(200), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.isZooming = true;
        this.calculateAndSetZoom(this.zoomConfig.zoomOutIncessantly);
      });
  }

  public handleZoomClick(zoomType: Zoom, mouseLeave?: boolean): void {
    this.unsubscribe$.next();
    if (this.isZooming) {
      this.isZooming = false;
      return;
    }
    if (mouseLeave) {
      return;
    }
    this.fetchZoomType(zoomType);
  }

  private calculateAndSetZoom({
    duration,
    zoomUnit,
    isZoomIn,
    isZoomToFit,
  }: ZoomConfig): void {
    if (isZoomToFit) {
      this.minimalZoom = this.game.canvas.clientWidth / imageSize.w;
      this.zoom = this.minimalZoom;
    } else {
      const zoom = (zoomUnit as number) + this.zoom / 10;
      this.zoom += isZoomIn ? zoom : -zoom;
      if (this.zoom > this.maximalZoom || this.zoom < this.minimalZoom) {
        this.zoom = isZoomIn ? this.maximalZoom : this.minimalZoom;
      }
    }
    this.cameras.main.zoomTo(this.zoom, duration);
  }

  private fetchZoomType(zoomType: Zoom) {
    switch (zoomType) {
      case Zoom.In:
        this.calculateAndSetZoom(this.zoomConfig.zoomIn);
        break;
      case Zoom.InIncessantly:
        this.zoomInIncessantly();
        break;
      case Zoom.Out:
        this.calculateAndSetZoom(this.zoomConfig.zoomOut);
        break;
      case Zoom.OutIncessantly:
        this.zoomOutIncessantly();
        break;
      case Zoom.ZoomToFit:
        this.calculateAndSetZoom(this.zoomConfig.zoomToFit);
        break;
    }
  }

  private initialize() {
    this.screen = new MainScreen(this);
    this.add.existing(this.screen);
    this.screen.setPosition(0, 0);
    this.cameras.main.setBounds(
      0,
      0,
      imageSize.w,
      imageSize.h + (imageSize.groundSize || 0)
    );
    this.fetchAndSetMinimalZoom();
  }

  public fetchAndSetMinimalZoom(): void {
    this.minimalZoom = Math.max(
      this.game.canvas.clientWidth / imageSize.w,
      this.zoom
    );
    this.cameras.main.setZoom(this.minimalZoom);
  }
}
